import { dashesOrNum, formatPercentage } from '@cast/utils';

import type {
  CpuChartData,
  GpuChartData,
  StorageChartData,
} from './gaugeChartData';

export type ResourceSubMetricView = {
  label: string;
  primaryValue: string;
  secondaryValue: string;
  markerColor: string;
};

export type ResourceMetricView = {
  title: string;
  unit: string;
  provisioned: string;
  segments: Array<{ value: number; color: string }>;
  benchmarkPercent?: number;
  subMetrics: ResourceSubMetricView[];
};

type ResourceColors = {
  used: string;
  allocatableBorder: string;
  requested: string;
  overhead: string;
  gpuIdle: string;
  gpuNotUsed: string;
  segmentUsed: string;
  segmentRequested: string;
  segmentAllocatable: string;
  segmentOverhead: string;
  segmentGpuIdle: string;
  segmentGpuNotUsed: string;
};

const fmt = (value: number, fractionDigits = 2): string =>
  String(
    dashesOrNum({ value, roundBy: fractionDigits, decimals: fractionDigits })
  );

const fmtPct = (value: number): string => formatPercentage(value) ?? '0%';

type ClampPctArgs = {
  value: number;
};

const clampPct = ({ value }: ClampPctArgs): number =>
  Math.min(100, Math.max(0, value));

/**
 * Classic console gauge parity for bars (`total={100}`):
 * outerLayer = used + (requested − used); appendix = overhead.
 * Allocatable is legend-only (unfilled track = free capacity).
 */
type TakeSegmentArgs = {
  remaining: { value: number };
  rawValue: number;
  color: string;
};

const takeSegment = ({
  remaining,
  rawValue,
  color,
}: TakeSegmentArgs): { value: number; color: string } | null => {
  const value = Math.min(remaining.value, Math.max(0, rawValue));
  if (value <= 0) {
    return null;
  }
  remaining.value -= value;
  return { value, color };
};

type BuildComputeSegmentsArgs = {
  data: CpuChartData;
  colors: ResourceColors;
};

const buildComputeSegments = ({
  data,
  colors,
}: BuildComputeSegmentsArgs): Array<{ value: number; color: string }> => {
  const remaining = { value: 100 };
  return [
    takeSegment({
      remaining,
      rawValue: data.usedPercentage,
      color: colors.segmentUsed,
    }),
    takeSegment({
      remaining,
      rawValue: data.requestedPercentage - data.usedPercentage,
      color: colors.segmentRequested,
    }),
    takeSegment({
      remaining,
      rawValue: data.overheadPercentage,
      color: colors.segmentOverhead,
    }),
  ].filter(
    (segment): segment is { value: number; color: string } => segment != null
  );
};

const buildSubMetrics = (
  data: CpuChartData,
  colors: ResourceColors
): ResourceSubMetricView[] => [
  {
    label: 'Used',
    primaryValue: fmt(data.used),
    secondaryValue: fmtPct(data.usedPercentage),
    markerColor: colors.used,
  },
  {
    label: 'Allocatable',
    primaryValue: fmt(data.allocatable),
    secondaryValue: fmtPct(data.allocatablePercentage),
    markerColor: colors.allocatableBorder,
  },
  {
    label: 'Requested',
    primaryValue: fmt(data.requested),
    secondaryValue: fmtPct(data.requestedPercentage),
    markerColor: colors.requested,
  },
  {
    label: 'Overhead',
    primaryValue: fmt(data.overhead),
    secondaryValue: fmtPct(data.overheadPercentage),
    markerColor: colors.overhead,
  },
];

/**
 * Classic GpuWidget: exporter → notUsed + idle + used;
 * otherwise used + absolute requested.
 */
type BuildGpuSegmentsArgs = {
  data: GpuChartData;
  colors: ResourceColors;
};

const buildGpuSegments = ({
  data,
  colors,
}: BuildGpuSegmentsArgs): Array<{ value: number; color: string }> => {
  const remaining = { value: 100 };
  const hasGpuBreakdown = data.idlePercentage > 0 || data.notUsedPercentage > 0;

  if (hasGpuBreakdown) {
    return [
      takeSegment({
        remaining,
        rawValue: data.notUsedPercentage,
        color: colors.segmentGpuNotUsed,
      }),
      takeSegment({
        remaining,
        rawValue: data.idlePercentage,
        color: colors.segmentGpuIdle,
      }),
      takeSegment({
        remaining,
        rawValue: data.usedPercentage,
        color: colors.segmentUsed,
      }),
    ].filter(
      (segment): segment is { value: number; color: string } => segment != null
    );
  }

  return [
    takeSegment({
      remaining,
      rawValue: data.usedPercentage,
      color: colors.segmentUsed,
    }),
    takeSegment({
      remaining,
      rawValue: data.requestedPercentage,
      color: colors.segmentRequested,
    }),
  ].filter(
    (segment): segment is { value: number; color: string } => segment != null
  );
};

export const mapCpuGaugeToResourceMetric = (
  data: CpuChartData | undefined,
  colors: ResourceColors
): ResourceMetricView | undefined => {
  if (!data?.provisioned) {
    return undefined;
  }

  return {
    title: 'CPU',
    unit: 'CPU',
    provisioned: fmt(data.provisioned),
    segments: buildComputeSegments({ data, colors }),
    benchmarkPercent: Math.round(clampPct({ value: data.requestedPercentage })),
    subMetrics: buildSubMetrics(data, colors),
  };
};

export const mapMemoryGaugeToResourceMetric = (
  data: CpuChartData | undefined,
  colors: ResourceColors
): ResourceMetricView | undefined => {
  if (!data?.provisioned) {
    return undefined;
  }

  return {
    title: 'Memory',
    unit: 'GiB',
    provisioned: fmt(data.provisioned),
    segments: buildComputeSegments({ data, colors }),
    benchmarkPercent: Math.round(clampPct({ value: data.requestedPercentage })),
    subMetrics: buildSubMetrics(data, colors),
  };
};

export const mapGpuGaugeToResourceMetric = (
  data: GpuChartData | undefined,
  colors: ResourceColors
): ResourceMetricView | undefined => {
  if (!data?.provisioned) {
    return undefined;
  }

  return {
    title: 'GPU',
    unit: 'GPU',
    provisioned: fmt(data.provisioned),
    segments: buildGpuSegments({ data, colors }),
    benchmarkPercent: Math.round(clampPct({ value: data.requestedPercentage })),
    subMetrics: [
      {
        label: 'Used',
        primaryValue: fmt(data.used),
        secondaryValue: fmtPct(data.usedPercentage),
        markerColor: colors.used,
      },
      {
        label: 'Allocatable',
        primaryValue: fmt(data.allocatable),
        secondaryValue: fmtPct(data.allocatablePercentage),
        markerColor: colors.allocatableBorder,
      },
      {
        label: 'Requested',
        primaryValue: fmt(data.requested),
        secondaryValue: fmtPct(data.requestedPercentage),
        markerColor: colors.requested,
      },
      {
        label: 'Idle',
        primaryValue: fmt(data.idle),
        secondaryValue: fmtPct(data.idlePercentage),
        markerColor: colors.gpuIdle,
      },
      {
        label: 'Not used',
        primaryValue: fmt(data.notUsed),
        secondaryValue: fmtPct(data.notUsedPercentage),
        markerColor: colors.gpuNotUsed,
      },
    ],
  };
};

export const mapStorageGaugeToResourceMetric = (
  data: StorageChartData | undefined,
  colors: ResourceColors
): ResourceMetricView | undefined => {
  if (!data?.provisioned) {
    return undefined;
  }

  const requested = clampPct({ value: data.requestedPercentage });

  return {
    title: 'Storage',
    unit: 'GiB',
    provisioned: fmt(data.provisioned),
    segments:
      requested > 0
        ? [{ value: requested, color: colors.segmentRequested }]
        : [],
    benchmarkPercent: Math.round(requested),
    subMetrics: [
      {
        label: 'Requested',
        primaryValue: fmt(data.requested),
        secondaryValue: fmtPct(data.requestedPercentage),
        markerColor: colors.requested,
      },
      {
        label: 'Provisioned',
        primaryValue: fmt(data.provisioned),
        secondaryValue: '100%',
        markerColor: colors.segmentAllocatable,
      },
    ],
  };
};

export const applyOperationalStateToResourceMetric = (
  metric: ResourceMetricView | undefined,
  isOperational: boolean
): ResourceMetricView | undefined => {
  if (!metric || isOperational) {
    return metric;
  }

  return {
    ...metric,
    provisioned: '--',
    segments: [],
    benchmarkPercent: undefined,
    subMetrics: metric.subMetrics.map((subMetric) => ({
      ...subMetric,
      primaryValue: '--',
      secondaryValue: '--',
    })),
  };
};
